import { CartFulfillGuardTypes, CartUpdateActionTypes } from "@checkout/index";
import type { BaseAction, BaseActionObject } from "xstate";
import {
  CartFulfillState,
  CartQuoteState,
  type ChildMachineConfig,
} from "../../../types";
import { CartQuoteActionTypes, CartQuoteEventType } from "../CartQuote";
import { ActionTypes as QuoteActions } from "../CartQuote/actions";
import { CartState } from "../types";
import { ActionTypes } from "./actions";
import { CartFulfillEventType } from "./events";
import { ServiceTypes } from "./services";

export type GenericActionType<MachineContext> = BaseAction<
  MachineContext,
  any,
  BaseActionObject,
  any
>;

export type CartFulfillConfig<MachineContext> = ChildMachineConfig & {
  trackPollSuccessAction?: GenericActionType<MachineContext>;
  trackPollFailureAction?: GenericActionType<MachineContext>;
};

export const getPollOnDoneActions = <MachineContext>({
  trackPollSuccessAction,
}: CartFulfillConfig<MachineContext>): Array<
  GenericActionType<MachineContext>
> => {
  const onDoneActions = [
    ActionTypes.setCartFulfillResult,
    ActionTypes.setCartFulfillCallStateSuccess,
    CartQuoteActionTypes.clearCartContext,
    CartQuoteActionTypes.clearCartQuoteProducts,
    // Definitive success: replace the pending stay with a completed record
    // (a no-op unless the tenant enabled the warning).
    ActionTypes.completeDuplicateBooking,
  ];
  return trackPollSuccessAction
    ? [trackPollSuccessAction, ...onDoneActions]
    : onDoneActions;
};

export const getPollOnErrorActions = <MachineContext>({
  trackPollFailureAction,
}: Partial<CartFulfillConfig<MachineContext>>): Array<
  GenericActionType<MachineContext>
> => {
  const onErrorActions = [
    ActionTypes.setCartFulfillError,
    ActionTypes.setCartFulfillCallStateFailed,
  ];
  return trackPollFailureAction
    ? [...onErrorActions, trackPollFailureAction]
    : onErrorActions;
};

export const getMachineState = <MachineContext>(
  config: CartFulfillConfig<MachineContext>
) => ({
  id: CartState.cartFulfill,
  initial: CartFulfillState.schedule,
  states: {
    [CartFulfillState.schedule]: {
      entry: ActionTypes.setCartFulfillCallStateInProcess,
      invoke: {
        src: ServiceTypes.scheduleFulfillService,
        onDone: {
          actions: [
            ActionTypes.setCartFulfillCipherText,
            // Fulfillment is now committed server-side; mark the stay pending so
            // a reload-then-rebook is caught (no-op unless the tenant enabled
            // the warning).
            ActionTypes.recordPendingDuplicateBooking,
            // This is to avoid discarding the quote on fulfillment, once scheduled a quote cannot be mutated/discarded
            QuoteActions.clearCartQuoteCipherText,
          ],
          target: CartFulfillState.polling,
        },
        onError: {
          actions: [
            ActionTypes.setCartFulfillError,
            ActionTypes.setCartFulfillCallStateFailed,
            // This is to avoid discarding the quote on fulfillment, once scheduled a quote cannot be mutated/discarded
            QuoteActions.clearCartQuoteCipherText,
          ],
          target: CartFulfillState.error,
        },
      },
    },
    [CartFulfillState.polling]: {
      invoke: {
        src: ServiceTypes.pollFulfillService,
        onDone: {
          actions: getPollOnDoneActions<MachineContext>(config),
          target: config.nextState,
        },
        onError: {
          actions: getPollOnErrorActions<MachineContext>(config),
          target: CartFulfillState.error,
        },
      },
    },
    [CartFulfillState.pollInteractive]: {
      invoke: {
        src: ServiceTypes.pollInteractivePaymentAuthService,
        onDone: {
          actions: [ActionTypes.setCartFulfillPendingInteractive],
          target: CartFulfillState.interactiveRoute,
        },
        onError: {
          actions: getPollOnErrorActions<MachineContext>(config),
          target: CartFulfillState.error,
        },
      },
    },
    [CartFulfillState.interactiveRoute]: {
      always: [
        {
          target: CartFulfillState.pollUserActionRoute,
          cond: CartFulfillGuardTypes.hasNoAction,
        },
        { target: CartFulfillState.challenge },
      ],
    },
    [CartFulfillState.challenge]: {
      on: {
        [CartFulfillEventType.COMPLETE_INTERACTIVE_SUCCESS]: {
          actions: [
            ActionTypes.clearCartFulfillPendingInteractive,
            ActionTypes.setCartFulfillCompleteInteractive,
          ],
          target: CartFulfillState.completeInteractive,
        },
        [CartFulfillEventType.COMPLETE_INTERACTIVE_ERROR]: {
          actions: [
            ActionTypes.clearCartFulfillPendingInteractive,
            ...getPollOnErrorActions<MachineContext>(config),
          ],
          target: CartFulfillState.error,
        },
      },
    },
    [CartFulfillState.completeInteractive]: {
      entry: ActionTypes.setCartFulfillCallStateInProcess,
      invoke: {
        src: ServiceTypes.completeInteractiveFulfillService,
        onDone: {
          target: CartFulfillState.pollUserActionRoute,
        },
        onError: {
          actions: getPollOnErrorActions<MachineContext>(config),
          target: CartFulfillState.error,
        },
      },
    },
    [CartFulfillState.pollUserActionRoute]: {
      always: {
        target: CartFulfillState.polling,
      },
    },
    [CartFulfillState.userActionRoute]: {
      always: [
        {
          target: CartFulfillState.polling,
          cond: CartFulfillGuardTypes.hasNoUserAction,
        },
        { target: CartFulfillState.userAction },
      ],
    },
    [CartFulfillState.userAction]: {
      on: {
        [CartFulfillEventType.USER_ACTION_SUCCESS]: {
          actions: [ActionTypes.clearCartFulfillUserActions],
          target: CartFulfillState.polling,
        },
        [CartFulfillEventType.USER_ACTION_ERROR]: {
          actions: [ActionTypes.clearCartFulfillUserActions],
          target: CartFulfillState.polling,
        },
      },
    },
    [CartFulfillState.error]: {
      ...(config.failedState ? { always: { target: config.failedState } } : {}),
    },
    // Only merges first layer object, doesn't do a deep merge
    // See ChildMachineConfig for more details
    ...(config.stateOverride ? config.stateOverride : {}),
  },
  on: {
    [CartFulfillEventType.CLEAR_CART_FULFILL_ERROR]: {
      actions: ActionTypes.clearCartFulfillError,
    },
    [CartFulfillEventType.COMPLETE_INTERACTIVE_ERROR]: {
      actions: [
        ActionTypes.clearCartFulfillPendingInteractive,
        ...getPollOnErrorActions<MachineContext>(config),
      ],
      target: `#${config.parentMachineId}.${CartState.cartFulfill}.${CartFulfillState.error}`,
    },
    [CartQuoteEventType.RETRY_QUOTE]: {
      target: `#${config.parentMachineId}.${CartState.cartQuote}.${CartQuoteState.route}`,
      actions: [
        CartQuoteActionTypes.setQuoteRetries,
        CartQuoteActionTypes.clearCartQuoteError,
        CartUpdateActionTypes.clearCartUpdateError,
      ],
    },
    [CartFulfillEventType.PREVIOUS]: {
      target: config.previousState,
    },
  },
});
